import { api } from "./client";

const DEFAULT_SEASON_ID = 5;

export async function getCurrentSeason() {
  const resp = await api.get("/games/premier_league/seasons/current");
  return resp.data; // { id, name, start_date, end_date }
}

export async function getPlSetupStatus() {
  const resp = await api.get("/games/premier_league/seasons/setup-status");
  return resp.data; // { setup_available, next_season_label, next_season_start, ... }
}

export async function getCurrentMatchweek(seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/current-matchweek`
  );
  return resp.data;
}

export async function getMatchweekSummary(
  poolId,
  matchweek,
  seasonId = DEFAULT_SEASON_ID
) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/matchweeks/${matchweek}/summary`
  );
  return resp.data;
}

export async function getLeaderboard(
  poolId,
  seasonId = DEFAULT_SEASON_ID
) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/leaderboard/`
  );
  return resp.data;
}

export async function getHalfSeasonLeaderboard(
  poolId,
  seasonId = DEFAULT_SEASON_ID
) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/leaderboard/half`
  );
  return resp.data;
}

/** Every player's pick for every matchweek — the All Picks grid. */
export async function getAllPicks(poolId, seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/picks/all-picks`
  );
  return resp.data;
}

export async function getStandings(matchweek, seasonId = DEFAULT_SEASON_ID) {
  const params = { season_id: seasonId };
  // Omitted entirely rather than sent as null, which the API would reject.
  if (matchweek != null) params.matchweek = matchweek;
  const resp = await api.get(`/games/premier_league/standings/`, { params });
  return resp.data;
}

/**
 * The table as it currently stands — the newest matchweek on file.
 *
 * Standings are written once a round is complete, so the round being played
 * has no row yet and asking for it by number comes back empty. Every page
 * showing "the current table" wants this, and each having its own idea of
 * which matchweek to ask for is what let the Table Predictor sit on the
 * opening-day table all season.
 */
export async function getLatestStandings(seasonId = DEFAULT_SEASON_ID) {
  return getStandings(null, seasonId);
}

export async function getMyPicks(
  poolId,
  seasonId = DEFAULT_SEASON_ID
) {
  const response = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/picks/me`
  );
  return response.data;
}

export async function createPick(
  poolId,
  payload,
  seasonId = DEFAULT_SEASON_ID
) {
  const response = await api.post(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/picks/`,
    payload
  );
  return response.data;
}

export async function updatePick(
  poolId,
  pickId,
  payload,
  seasonId = DEFAULT_SEASON_ID
) {
  const response = await api.put(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/picks/${pickId}`,
    payload
  );
  return response.data;
}

export async function getLastClosedMatchweekPicks(
  poolId,
  seasonId = DEFAULT_SEASON_ID
) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/matchweeks/last-closed/picks`
  );
  return resp.data;
}

export async function getSmackMessages(poolId, seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/smack/`
  );
  return resp.data;
}

export async function postSmackMessage(poolId, body, seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.post(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/smack/`,
    { body }
  );
  return resp.data;
}

export async function getSmackReplies(poolId, messageId, seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.get(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/replies`
  );
  return resp.data;
}

export async function postSmackReply(poolId, messageId, body, seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.post(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/replies`,
    { body }
  );
  return resp.data;
}

export async function reactSmack(poolId, messageId, reaction, seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.post(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/react`,
    { reaction }
  );
  return resp.data;
}

export async function deleteSmackMessage(poolId, messageId, seasonId = DEFAULT_SEASON_ID) {
  const resp = await api.delete(
    `/games/premier_league/seasons/${seasonId}/pools/${poolId}/smack/${messageId}`
  );
  return resp.data;
}

export async function getMyPoolsHistory() {
  const resp = await api.get("/games/premier_league/my-pools/");
  return Array.isArray(resp.data) ? resp.data : [];
}
export async function getPlMatchupHistory(teamA, teamB, seasons = 10) {
  const resp = await api.get(`/games/premier_league/matchups/`, {
    params: { team_a: teamA, team_b: teamB, seasons },
  });
  return resp.data;
}
